import React, { useState } from "react";
import { makeStyles, useTheme } from "@mui/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import { Button } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { CSVLink } from "react-csv";
import { Bar } from "react-chartjs-2";

import API from "util/API";
import { showErrorSnackbar } from "store/actions/snackbarMessage";
import { useDispatch } from "react-redux";
import palette from "theme/palette";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  table: {},
  textLink: {
    textDecoration: "underline",
  },
  chartContainer: {
    height: 400,
    position: "relative",
    marginTop: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const PaxReport = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const showError = (msg) => dispatch(showErrorSnackbar(msg));

  const formatDate = (date) => moment(date).format("YYYY-MM-DD");

  const queryReport = async () => {
    if (endDate < startDate) {
      showError("The end date cannot be earlier than the start date!");
      return;
    }

    setIsLoading(true);
    try {
      const startDateStr = formatDate(startDate);
      const endDateStr = formatDate(endDate);
      const response = await API.get(
        `/paxReport?startDateStr=${startDateStr}&endDateStr=${endDateStr}`
      );
      const data = response.data;
      setReportData(data || []);
    } catch (err) {
      console.log(err);
      setReportData([]);
      showError("Something went wrong, please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const totalPax = reportData.reduce(
    (sum, item) => sum + (Number(item.totalGuests) || 0),
    0
  );
  const totalOrders = reportData.reduce(
    (sum, item) => sum + (Number(item.orderCount) || 0),
    0
  );
  const avgPaxPerOrder =
    totalOrders > 0 ? (totalPax / totalOrders).toFixed(1) : 0;

  const chartData = {
    labels: reportData.map((item) =>
      moment(item.date).format("DD/MM")
    ),
    datasets: [
      {
        label: "Total Guests (Pax)",
        data: reportData.map((item) => Number(item.totalGuests) || 0),
        backgroundColor: "rgba(54, 162, 235, 0.5)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
      {
        label: "Orders",
        data: reportData.map((item) => Number(item.orderCount) || 0),
        backgroundColor: "rgba(255, 159, 64, 0.5)",
        borderColor: "rgba(255, 159, 64, 1)",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        ticks: {
          color: palette.text?.secondary || "#666",
        },
        grid: {
          display: false,
        },
      },
      y: {
        ticks: {
          color: palette.text?.secondary || "#666",
          beginAtZero: true,
          stepSize: 1,
        },
        grid: {
          borderDash: [2],
          borderDashOffset: [2],
          color: palette.divider,
          drawBorder: false,
        },
      },
    },
  };

  const csvData = () => {
    const data = [["Date", "Total Guests (Pax)", "Orders", "Avg Pax/Order"]];
    reportData.forEach((item) => {
      const dateStr = moment(item.date).format("DD/MM/YYYY");
      const guests = Number(item.totalGuests) || 0;
      const orders = Number(item.orderCount) || 0;
      const avg = orders > 0 ? (guests / orders).toFixed(1) : "0";
      data.push([dateStr, guests, orders, avg]);
    });
    data.push(["Total", totalPax, totalOrders, avgPaxPerOrder]);
    return data;
  };

  return (
    <div className={classes.root}>
      {isLoading && <LinearProgress style={{ marginBottom: 8 }} />}
      <Grid container spacing={2}>
        <Grid
          item
          lg={8}
          md={12}
          xl={9}
          xs={12}
          container
          direction="row"
          alignItems="center"
        >
          <Typography style={{ margin: theme.spacing(1) }}>
            Start Date
          </Typography>
          <DatePicker
            selected={startDate}
            dateFormat="dd/MM/yyyy"
            onChange={(date) => setStartDate(date)}
          />
          <Typography style={{ margin: theme.spacing(1) }}>
            End Date
          </Typography>
          <DatePicker
            selected={endDate}
            dateFormat="dd/MM/yyyy"
            onChange={(date) => setEndDate(date)}
          />

          <Button
            color="primary"
            variant="contained"
            style={{ marginLeft: theme.spacing(1) }}
            onClick={queryReport}
          >
            Query
          </Button>
        </Grid>

        {reportData.length > 0 && (
          <>
            <Grid item lg={8} md={12} xl={9} xs={12}>
              <Paper className={classes.summaryCard}>
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    <Typography variant="h6" color="textSecondary">
                      Total Pax
                    </Typography>
                    <Typography variant="h4">{totalPax}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6" color="textSecondary">
                      Total Dine-in Orders
                    </Typography>
                    <Typography variant="h4">{totalOrders}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6" color="textSecondary">
                      Avg Pax / Order
                    </Typography>
                    <Typography variant="h4">{avgPaxPerOrder}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item lg={8} md={12} xl={9} xs={12}>
              <div className={classes.chartContainer}>
                <Bar data={chartData} options={chartOptions} />
              </div>
            </Grid>

            <Grid item lg={8} md={12} xl={9} xs={12}>
              <CSVLink
                filename={`pax-report-${formatDate(startDate)}-to-${formatDate(endDate)}.csv`}
                data={csvData()}
              >
                <Typography variant="h5" className={classes.textLink}>
                  Download CSV
                </Typography>
              </CSVLink>

              <TableContainer component={Paper} style={{ marginTop: 16 }}>
                <Table className={classes.table} aria-label="pax report table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Total Guests (Pax)</TableCell>
                      <TableCell align="right">Dine-in Orders</TableCell>
                      <TableCell align="right">Avg Pax / Order</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.map((item) => {
                      const guests = Number(item.totalGuests) || 0;
                      const orders = Number(item.orderCount) || 0;
                      const avg =
                        orders > 0 ? (guests / orders).toFixed(1) : "-";
                      return (
                        <TableRow key={item.date}>
                          <TableCell>
                            {moment(item.date).format("DD/MM/YYYY")}
                          </TableCell>
                          <TableCell align="right">{guests}</TableCell>
                          <TableCell align="right">{orders}</TableCell>
                          <TableCell align="right">{avg}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell>
                        <strong>Total</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{totalPax}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{totalOrders}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{avgPaxPerOrder}</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </>
        )}
      </Grid>
    </div>
  );
};

export default PaxReport;
